import React, { useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import block from 'bem-cn';
import dayjs from 'dayjs';

import PaginatorSwitcher from 'components/Paginator/PaginatorSwitcher/desktop';
import Select from 'components/Select/desktop';
import Button from 'components/Button';
import { formateNumber } from 'shared/helpers';

import TableRow from '../TableRow/TableRow';
import TableHead from '../TableHead/TableHead';

import './Table.scss';

const b = block('payment-history-table-cash');

const Table = ({ currentPage, onPageClick, onItemsOnPageChanged, setIsOnlyTotal, isOnlyTotal }) => {
  const totalCount = useSelector(state => state.reports.paymentHistory.details.totalCount, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const { paymentHistory, paymentHistoryTotals } = useSelector(state => state.reports, shallowEqual);
  const itemsOnPageFilters = [
    { value: 10, name: '10' },
    { value: 25, name: '25' },
    { value: 50, name: '50' },
    { value: 100, name: '100' },
  ];
  const [itemsOnPage, setItemsOnPage] = useState({ value: 10, name: '10' });

  const selectItemChangedHandler = itemValue => {
    setItemsOnPage({ value: itemValue, name: itemValue });
    onItemsOnPageChanged(itemValue);
  };

  const rows = useMemo(
    () => paymentHistory.transfers.map(item => <TableRow key={item.id} item={item} locale={locale} />),
    [paymentHistory, locale],
  );

  return (
    <div className={b()}>
      <div className={b('update-time')}>
        {paymentHistoryTotals.lastCalculateTime &&
          `${locale.lastUpdateTime}: ${paymentHistoryTotals.lastCalculateTime}`}
      </div>
      <div className={b('total-header')}>{locale.total}</div>
      <div className={b('totals')}>
        <div className={b('totals-item')}>
          <div className={b('totals-head')}>{locale.totalDeposit}</div>
          <div className={b('totals-data')}>{paymentHistoryTotals.totalDeposit}</div>
        </div>
        <div className={b('totals-item')}>
          <div className={b('totals-head')}>{locale.totalWithdrawal}</div>
          <div className={b('totals-data')}>{paymentHistoryTotals.totalWithdraw}</div>
        </div>
        <div className={b('totals-item')}>
          <div className={b('totals-head')}>{locale.balance}</div>
          <div className={b('totals-data')}>{paymentHistoryTotals.deltaBalance}</div>
        </div>
      </div>
      {!isOnlyTotal ? (
        <>
          <div className={b('header')}>
            <span className={b('header-left')}>{locale.result}:</span>
            <div className={b('page-count')}>
              <div className={b('page-count-title')}>{locale.elementsOnPage}:</div>
              <div className={b('page-count-select')}>
                <Select
                  paginator
                  items={itemsOnPageFilters}
                  activeItem={itemsOnPage}
                  onChange={selectItemChangedHandler}
                  color="grey"
                />
              </div>
            </div>
          </div>
          <div className={b('table')}>
            <TableHead locale={locale} />
            <div className={b('tbody')}>
              {!!rows.length ? rows : <div className={b('no-results')}>{locale.noResults}</div>}
            </div>
            <div className={b('paginator-wrapper')}>
              <div className={b('extra-info')}>
                {(paymentHistory.details.totalCount || 0)} {locale.records}
              </div>
              <PaginatorSwitcher
                count={totalCount}
                currentPage={currentPage}
                onPageClick={onPageClick}
                itemsOnPage={itemsOnPage}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={b('button')}>
          <Button size="low" callBack={() => setIsOnlyTotal(false)} text={locale.loadOperations} />
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  count: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onItemsOnPageChanged: PropTypes.func,
  onPageClick: PropTypes.func.isRequired,
};

export default Table;
